import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import API from "../lib/api";
import { Warning, UserMinus, ArrowRight, CalendarBlank, CheckCircle, Lightning, Briefcase, ListChecks } from "@phosphor-icons/react";

/**
 * Phase 286 — "Unassigned This Week" widget
 *
 * Mirrors the amber "⚠ Unassigned" warning from the Monday digest, but live on
 * the admin dashboard. Lists every job / emergency / task scheduled in the
 * next 7 days that has no assignee. Click → opens the entity so admin can
 * assign it in seconds.
 */

const TYPE_META = {
  emergency: { label: "Emergency", Icon: Lightning, cls: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300" },
  job: { label: "Job", Icon: Briefcase, cls: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300" },
  task: { label: "Task", Icon: ListChecks, cls: "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300" },
};

function formatShort(dateStr) {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
  } catch { return dateStr; }
}

export default function UnassignedThisWeekWidget() {
  const [data, setData] = useState({ items: [], total: 0, window_start: "", window_end: "" });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await API.get("/dashboard/unassigned-this-week");
      setData(data);
    } catch {
      // non-fatal
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 5 * 60_000); // every 5 minutes
    return () => clearInterval(t);
  }, [load]);

  const items = data.items || [];

  return (
    <div className="border border-border rounded-md bg-card animate-fade-in" data-testid="widget-unassigned-this-week">
      <div className="px-4 py-3 border-b border-border flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <UserMinus size={16} className="text-amber-500" weight="fill" />
          <h3 className="font-semibold text-sm font-[Chivo]">
            Unassigned This Week
            <span className="ml-2 text-xs font-normal text-muted-foreground">(next 7 days)</span>
          </h3>
        </div>
        <span className="text-[11px] text-muted-foreground" data-testid="unassigned-total">
          <strong className={items.length > 0 ? "text-amber-600 dark:text-amber-400" : "text-foreground"}>{items.length}</strong> unassigned
        </span>
      </div>

      {loading && items.length === 0 ? (
        <p className="px-4 py-6 text-xs text-muted-foreground text-center" data-testid="unassigned-loading">Loading…</p>
      ) : items.length === 0 ? (
        <div className="px-4 py-6 flex items-center justify-center gap-2 text-emerald-600 dark:text-emerald-400" data-testid="unassigned-empty">
          <CheckCircle size={16} weight="fill" />
          <p className="text-xs font-medium">Everything booked this week is assigned. ✨</p>
        </div>
      ) : (
        <ul className="divide-y divide-border" data-testid="unassigned-list">
          {items.slice(0, 8).map(it => {
            const meta = TYPE_META[it.type] || TYPE_META.job;
            const Icon = meta.Icon;
            return (
              <li key={`${it.type}-${it.id}`} data-testid={`unassigned-item-${it.id}`}>
                <Link
                  to={it.href}
                  className="flex items-center gap-3 px-4 py-2.5 row-hover transition-colors"
                  data-testid={`unassigned-link-${it.id}`}
                >
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold shrink-0 ${meta.cls}`}>
                    <Icon size={11} weight="bold" />
                    {meta.label}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-xs font-medium truncate">{it.title}</p>
                      <span className="text-[10px] font-mono text-muted-foreground">{it.reference}</span>
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      {formatShort(it.date)}{it.time ? ` · ${it.time}` : ""}
                      {it.location ? ` · ${it.location}` : ""}
                    </p>
                  </div>
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-900/40 px-1.5 py-0.5 rounded shrink-0">
                    <Warning size={10} weight="fill" /> Assign
                  </span>
                  <ArrowRight size={12} className="text-muted-foreground shrink-0" />
                </Link>
              </li>
            );
          })}
          {items.length > 8 && (
            <li className="px-4 py-2 text-[10px] text-muted-foreground text-center" data-testid="unassigned-overflow">
              +{items.length - 8} more — open <Link to="/calendar" className="text-primary hover:underline">Schedule</Link> to review
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
